import React, {cloneElement, useEffect, useId, useRef} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';

import {Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useMobileAnchoredTooltip} from '@amblique/king-living/app/hooks/use-mobile-anchored-tooltip';

function setRef(ref, node) {
  if (typeof ref === 'function') ref(node);
  else if (ref) ref.current = node;
}

/**
 * Mobile tap tooltip — fixed panel anchored to trigger; closes on outside tap or Esc.
 * When interactive, panel accepts text selection; scroll does not auto-close.
 */
const MobileAnchoredTooltip = ({
  children,
  interactive = false,
  isOpen,
  label,
  onClose,
  onToggle,
}) => {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const tooltipId = useId();
  const styles = useMultiStyleConfig('MobileAnchoredTooltip', {
    variant: interactive ? 'interactive' : 'default',
  });
  const position = useMobileAnchoredTooltip(triggerRef, panelRef, isOpen, onClose, {
    closeOnScroll: !interactive,
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleTriggerClick = (event) => {
    children.props.onClick?.(event);
    onToggle(event);
  };

  return (
    <>
      {cloneElement(children, {
        'aria-controls': interactive && isOpen ? tooltipId : undefined,
        'aria-describedby': !interactive && isOpen ? tooltipId : undefined,
        'aria-expanded': isOpen,
        'onClick': handleTriggerClick,
        'ref': (node) => {
          triggerRef.current = node;
          setRef(children.ref, node);
        },
      })}
      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <Box
            ref={panelRef}
            aria-modal={interactive ? false : undefined}
            className="mobile-anchored-tooltip__panel"
            id={tooltipId}
            role={interactive ? 'dialog' : 'tooltip'}
            visibility={position.isReady ? 'visible' : 'hidden'}
            {...styles.root}
            left={`${position.left}px`}
            top={`${position.top}px`}
          >
            {label}
            <Box
              className="mobile-anchored-tooltip__arrow"
              left={`${position.arrowLeft}px`}
              {...styles.arrow}
              aria-hidden
            />
          </Box>,
          document.body
        )}
    </>
  );
};

MobileAnchoredTooltip.displayName = 'MobileAnchoredTooltip';
MobileAnchoredTooltip.propTypes = {
  children: PropTypes.element.isRequired,
  interactive: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  label: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default MobileAnchoredTooltip;
